import { take } from 'rxjs/operators';
import { PurchaseOrderService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { withPagination, selectCurrentPageEntities, selectPaginationData, selectCurrentPage, updatePaginationData, setPage, skipWhilePageExists, setCurrentPage, getPaginationData } from '@ngneat/elf-pagination';
import { PurchaseOrder } from '@tcc-mono/vandijk/shared/data-access/models';
import { withEntities, selectActiveEntity, withActiveId, upsertEntities, setActiveId, hasEntity, getActiveId, deleteEntities, deleteAllEntities } from '@ngneat/elf-entities';
import { createStore } from '@ngneat/elf';
import { inject, Injectable } from '@angular/core';
import { createRequestDataSource, withRequestsStatus } from '@ngneat/elf-requests';
import { EMPTY, map, tap, identity, Observable } from 'rxjs';
import { ElfUtil } from '@tcc-mono/shared/utils';
import { PaginatedResponse, Response } from '@tcc-mono/shared/data-access';


const store = createStore(
  { name: 'purchaseOrders' },
  withEntities<PurchaseOrder>(),
  withPagination(),
  withActiveId(),
  withRequestsStatus()
);

const purchaseOrdersDataSource = createRequestDataSource({
  data$: () => store.pipe(selectCurrentPageEntities()),
  requestKey: 'purchaseOrders',
  dataKey: 'purchaseOrders',
  store
});

@Injectable({
  providedIn: 'root'
})
export class PurchaseOrdersRepository {
  private readonly _purchaseOrderService: PurchaseOrderService = inject(PurchaseOrderService);

  public purchaseOrders$ = store.pipe(
    selectCurrentPageEntities()
  );

  public paginationData$ = store.pipe(
    selectPaginationData()
  );

  public currentPage$ = store.pipe(
    selectCurrentPage()
  );

  public activePurchaseOrder$ = store.pipe(
    selectActiveEntity()
  );

  public loading$ = purchaseOrdersDataSource.data$().pipe(map(({ loading }) => loading));

  public setActivePurchaseOrder = (id: number) => {
    if (store.query(getActiveId) !== id) {
      store.update(
        setActiveId(id)
      );

      if (!store.query(hasEntity(id))) {
        this.getPurchaseOrder(id)
          .pipe(take(1))
          .subscribe();
      }

    }
  }

  public setPage = (page: number, refreshPage = false) => {
    store.update(
      setCurrentPage(page)
    );

    this._getPurchaseOrdersByPage(page, refreshPage)
      .pipe(take(1))
      .subscribe();
  }

  public resetRepo = (): void => {
    store.update(deleteAllEntities());
  }

  private _getPurchaseOrdersByPage = (page: number | string, refreshPage = false) => {
    return this._purchaseOrderService.getAll({ page })
      .pipe(
        tap(this._upsertPurchaseOrders),
        purchaseOrdersDataSource.trackRequestStatus(),
        refreshPage ? identity : skipWhilePageExists(store, page)
      );
  }

  public getPurchaseOrder = (id: number) => {
    const call = this._purchaseOrderService.get(id)
      .pipe(
        map(({ data }) => data),
        tap((purchaseOrder: PurchaseOrder) => store.update(upsertEntities(purchaseOrder)))
      );

    return store.query(hasEntity(id)) ? EMPTY : call;
  }

  private _upsertPurchaseOrders = ({ data, meta }: PaginatedResponse<PurchaseOrder>) => {
    const pagination = ElfUtil.convertMetaToElfPagination(meta);

    store.update(
      upsertEntities(data),
      updatePaginationData(pagination),
      setPage(
        pagination.currentPage,
        data.map(c => c.id)
      ),
      purchaseOrdersDataSource.setSuccess()
    );
  }

  public addPurchaseOrder = (purchaseOrder: PurchaseOrder): Observable<PurchaseOrder> => {
    return this._purchaseOrderService.post(purchaseOrder)
      .pipe(
        map(({ data }) => data as PurchaseOrder),
        tap((newPurchaseOrder) => {
          store.update(
            upsertEntities(newPurchaseOrder)
          );

          const { currentPage } = store.query(getPaginationData());

          this.setPage(
            currentPage,
            true
          );
        })
      );
  }

  public updatePurchaseOrder = (purchaseOrder: PurchaseOrder): Observable<PurchaseOrder> => {
    return this._purchaseOrderService.put(purchaseOrder.id, purchaseOrder)
      .pipe(
        map(({ data }) => data as PurchaseOrder),
        tap((updatedPurchaseOrder) => {
          store.update(
            upsertEntities(updatedPurchaseOrder)
          );
        })
      );
  }

  public removePurchaseOrder = (purchaseOrderId: string | number) => {
    return this._purchaseOrderService.delete(purchaseOrderId)
      .pipe(
        tap(() => {
          store.update(
            deleteEntities(+purchaseOrderId)
          );

          const { currentPage } = store.query(getPaginationData());

          this.setPage(
            currentPage,
            true
          );
        })
      )
  }

}
